import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import client from 'braintree-web/client';
import paypalCheckout, { PayPalCheckoutLoadPayPalSDKOptions } from 'braintree-web/paypal-checkout';

// Design System
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import env from 'jl-design-system/utils/env/env';

// Types
import { AppDispatch } from 'types/RootState.types';
import { PayPalButtonProps, PayPalType } from './PayPalButton.types';

// Config
import {
  paymentTypeConstantsMap,
  PayPalTypes,
  getPayPalSdkOptions,
  tokenHasJustBeenReceived,
  isLocalPayPalSandbox,
  useRealPayPal,
  resolvePayPalClientToken,
} from './PayPalButton.constants';
import payPalButtonState from './PayPalButton.state';
import PayPalStubsModal from '@non-prod-utils/components/paypal-stubs-modal';
import { showDisableSiteSpinner, hideDisableSiteSpinner } from '@redux/actions/app/appActions';
import { setSelectedPaymentType, triggerPlaceOrderAndPayStartAnalytics } from '@redux/actions/payment/paymentActions';
import {
  getPayPalClientToken,
  getPayPalPaymentRequest,
  openPayPalStubsModal,
  payPalPaymentOnApprove,
  payPalPaymentOnCancel,
  setPayPalServiceUnavailable,
  payPalPaymentOnError,
  getPayPalExpressPaymentRequest,
  setPayPalPayLaterIsNotEligible,
  shippingAddressChange,
} from '@redux/actions/payment/payPalPaymentActions';
import { saveTimePlaceOrderClicked } from '@redux/actions/timing/timingActions';
import { reportErrorToNewRelic, sendNewRelicCustomEvent } from '@utils/logging/logging-utils';
import { countryCodeIsInternational } from '@utils/address/addressHelpers';
import { modifyCreateOrderPayload } from '@utils/payment/modifyCreateOrderPayload';

// Styles
import styles from './paypal-button.scss';

const PayPalButton = ({
  braintree = { client, paypalCheckout },
  onLoad = () => {},
  type = 'paypal',
}: PayPalButtonProps) => {
  const dispatch: AppDispatch = useDispatch();
  const { getPayPalClientTokenCallActive, payPalClientToken, payPalStubsModalOpen, sessionId } =
    useSelector(payPalButtonState);
  const [payPalButton, setPayPalButton] = useState<any>(undefined);
  const prevPayPalButton = useRef<any>(undefined);
  const prevPayPalClientToken = useRef<string | undefined>(undefined);
  const shouldUseLocalPayPalSandbox = isLocalPayPalSandbox();

  useEffect(() => {
    onLoad();

    if (payPalButton) {
      renderButton();
    } else if (payPalClientToken) {
      generateButton(payPalClientToken);
    } else if (!getPayPalClientTokenCallActive) {
      dispatch(getPayPalClientToken());
    }

    prevPayPalButton.current = payPalButton;
    prevPayPalClientToken.current = payPalClientToken;
  }, []);

  useEffect(() => {
    if (buttonHasJustBeenGenerated(prevPayPalButton.current, payPalButton)) {
      renderButton();
    } else if (tokenHasJustBeenReceived(prevPayPalClientToken.current, payPalClientToken)) {
      generateButton(payPalClientToken);
    } else if (!payPalClientToken && !getPayPalClientTokenCallActive) {
      dispatch(getPayPalClientToken());
    }

    prevPayPalButton.current = payPalButton;
    prevPayPalClientToken.current = payPalClientToken;
  }, [payPalButton, payPalClientToken, getPayPalClientTokenCallActive]);

  const onClickHandler = () => {
    dispatch(saveTimePlaceOrderClicked());

    const analyticsProp = type;
    const paymentTypeConstantsValue = paymentTypeConstantsMap[type];

    dispatch(triggerPlaceOrderAndPayStartAnalytics(analyticsProp));

    dispatch(setSelectedPaymentType({ paymentType: paymentTypeConstantsValue }));

    if (!useRealPayPal()) {
      dispatch(openPayPalStubsModal());
    }
  };

  const buttonCustomisation = (payPalCheckoutInstance: paypalCheckout.PayPalCheckout) => {
    const fundingSourceMap: Record<PayPalType, any> = {
      paypal: window?.paypal?.FUNDING?.PAYPAL,
      paypalExpress: window?.paypal?.FUNDING?.PAYPAL,
      paypalPayLater: window?.paypal?.FUNDING?.PAYLATER,
    };
    const fundingSource = fundingSourceMap[type];

    const paypalButtonStyleBase = {
      layout: 'vertical',
      shape: 'rect',
      height: 40,
      tagline: false,
    };

    const buttonStyleCustomisationOptions = {
      ...paypalButtonStyleBase,
      color: 'gold',
      label: 'pay',
    };

    const createOrder = async () => {
      dispatch(showDisableSiteSpinner());

      let payPalPaymentRequest;
      if (type === PayPalTypes.Express) {
        payPalPaymentRequest = await dispatch(getPayPalExpressPaymentRequest());
        payPalPaymentRequest = payPalPaymentRequest.result;

        if (payPalPaymentRequest?.shippingOptions) {
          payPalPaymentRequest = modifyCreateOrderPayload(payPalPaymentRequest);
        }
      } else {
        payPalPaymentRequest = await dispatch(getPayPalPaymentRequest());
      }

      return payPalCheckoutInstance.createPayment(payPalPaymentRequest).catch(() => {});
    };

    const onApprove = async (data: paypalCheckout.PayPalCheckoutTokenizationOptions) => {
      dispatch(hideDisableSiteSpinner());

      const {
        details: { billingAddress, firstName, lastName, phone: phoneNumber, shippingAddress },
        nonce,
        shippingOptionId,
      } = (await payPalCheckoutInstance.tokenizePayment(data)) as any;

      const paymentData = {
        firstName,
        lastName,
        phoneNumber,
        billingAddress,
        nonce,
        ...(type === PayPalTypes.Express && { shippingAddress, shippingOptionId }),
      };

      await dispatch(payPalPaymentOnApprove(paymentData));
    };

    const onCancel = async () => {
      dispatch(hideDisableSiteSpinner());
      await dispatch(payPalPaymentOnCancel());
    };

    const onError = async () => {
      dispatch(hideDisableSiteSpinner());
      await dispatch(payPalPaymentOnError());
    };

    const onShippingAddressChange = async (data: any, actions: any) => {
      if (countryCodeIsInternational(data.shippingAddress.countryCode)) {
        return actions.reject(data.errors.COUNTRY_ERROR);
      }
      try {
        await dispatch(shippingAddressChange(data));
        const paymentRequest = await dispatch(getPayPalExpressPaymentRequest());
        if (paymentRequest?.result) {
          const updatedShippingOptions = paymentRequest?.result?.shippingOptions;
          const { amount, currency } = paymentRequest.result;
          const { orderID } = data;

          if (payPalCheckoutInstance && payPalCheckoutInstance.updatePayment) {
            await payPalCheckoutInstance.updatePayment({
              paymentId: orderID,
              amount: amount,
              currency,
              shippingOptions: updatedShippingOptions,
            });
          } else {
            /* eslint-disable-next-line no-console */
            console.error('payPalCheckoutInstance is not initialized or lacks updatePayment.');
          }
        } else {
          return actions.reject(data.errors.ADDRESS_ERROR);
        }
      } catch (error: any) {
        /* eslint-disable-next-line no-console */
        console.error('Error in onShippingAddressChange:', error);
        return actions.reject(error?.code);
      }
    };

    const buttonCustomisationOptions: any = {
      fundingSource,
      style: buttonStyleCustomisationOptions,
      createOrder,
      onApprove,
      onCancel,
      onError,
      onClick: onClickHandler,
      ...(type === PayPalTypes.Express && { onShippingAddressChange }),
    };

    return buttonCustomisationOptions;
  };

  const generateButton = (payPalClientToken: string | undefined) => {
    const token = resolvePayPalClientToken({ payPalClientToken });

    const buttonRenderTimeout = setTimeout(() => {
      const spinner = document.querySelector('[data-testid="paypal-spinner-container"]');
      if (spinner) {
        dispatch(setPayPalServiceUnavailable());
      }
    }, 10000);

    const paypalSDKOptions: PayPalCheckoutLoadPayPalSDKOptions = getPayPalSdkOptions({ type });

    if (!useRealPayPal()) {
      clearTimeout(buttonRenderTimeout);
      setPayPalButton('button');
    } else if (token && braintree?.client?.create && braintree?.paypalCheckout?.create) {
      braintree.client
        .create({ authorization: token })
        .then((braintreeClientInstance) => braintree.paypalCheckout.create({ client: braintreeClientInstance }))
        .then((paypalCheckoutInstance) => paypalCheckoutInstance.loadPayPalSDK(paypalSDKOptions))
        .then((paypalCheckoutInstance) => {
          if (typeof window.paypal?.Buttons !== 'function') {
            throw new Error('PayPal Buttons is undefined');
          }
          return window.paypal.Buttons(buttonCustomisation(paypalCheckoutInstance));
        })
        .then((payPalButton) => {
          clearTimeout(buttonRenderTimeout);
          setPayPalButton(payPalButton);
        })
        .catch((error) => {
          clearTimeout(buttonRenderTimeout);
          dispatch(
            reportErrorToNewRelic({
              error,
              errorDescription: 'Paypal error',
            }),
          );
          dispatch(setPayPalServiceUnavailable());
        });
    } else {
      clearTimeout(buttonRenderTimeout);
      dispatch(setPayPalServiceUnavailable());
    }
  };

  const renderButton = () => {
    if ((!env.isClientLocal && !env.isClientDev) || shouldUseLocalPayPalSandbox) {
      if (payPalButton.isEligible()) {
        payPalButton.render(`#paypal-button-container__${type}`).catch((error: any) => {
          dispatch(
            reportErrorToNewRelic({
              error,
              errorDescription: 'Paypal error',
            }),
          );
          dispatch(setPayPalServiceUnavailable());
        });
      } else {
        dispatch(setPayPalPayLaterIsNotEligible());
        dispatch(sendNewRelicCustomEvent('paypal pay later is not eligible', { sessionId }));
      }
    }
  };

  const buttonHasJustBeenGenerated = (previousState: any, currentState: string | undefined) =>
    !previousState && currentState;

  if (!shouldUseLocalPayPalSandbox && (env.isClientLocal || env.isClientDev)) {
    return (
      <>
        <PrimaryButton className={styles.button} onClick={onClickHandler}>
          <span>Pay with PayPal</span>
        </PrimaryButton>

        {payPalStubsModalOpen && <PayPalStubsModal />}
      </>
    );
  }

  if (payPalButton) {
    return (
      <div className={styles.button} data-testid="paypal-button-container" id={`paypal-button-container__${type}`} />
    );
  }

  return (
    <div className={styles.spinnerWrap} data-testid="paypal-spinner-container">
      <LoadingSpinner className={styles.spinner} />
    </div>
  );
};

export default PayPalButton;
